import { TFunction } from "i18next";
import {
  EQ_PERIOD_LABEL_KEYS,
  formatCurrency,
  formatDateShort,
  getBillingFrequencyLabel
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { EqPaymentsBillingFrequency } from "@/pages/contracts/types";
import { TermsFieldChange } from "./types";

// The change set's field names, camelCased, as the backend's diffable set emits
// them. Raw values are meaningless to a reader: a date arrives as a timestamp,
// money as a bare number and a frequency as its enum ordinal.
const DATE_FIELDS = new Set([
  "serviceStartDate",
  "serviceEndDate",
  "customOptOutDate",
  "billingStartDate"
]);

const MONEY_FIELDS = new Set(["pricePerUnit", "total", "totalLocal"]);

const isEqPeriod = (value: number): value is EqPaymentsBillingFrequency =>
  Object.prototype.hasOwnProperty.call(EQ_PERIOD_LABEL_KEYS, value);

export interface DisplayContext {
  t: TFunction;
  // Money values are denominated in the contract's currency.
  currency: string | null;
  // Which unit a discount carries on this side of the change; a discount is a
  // percentage or an amount, and the field alone does not say which.
  isDiscountPercentage: boolean;
}

// Which unit a discount amount carries on one side of a change. The unit can
// change alongside the amount, and then each side carries its own; otherwise
// both sides carry the row's current unit.
export const discountUnitFor = (
  changes: TermsFieldChange[],
  side: "before" | "after",
  rowIsPercentage: boolean
): boolean => {
  const unitChange = changes.find(
    (change) => change.field === "isDiscountPercentage"
  );
  return typeof unitChange?.[side] === "boolean"
    ? (unitChange[side] as boolean)
    : rowIsPercentage;
};

// One before/after value as the tooltip shows it. Booleans reach here from
// fields like autoRenewal, so they are translated rather than stringified.
export const displayValue = (
  value: TermsFieldChange["before"],
  field: string,
  { t, currency, isDiscountPercentage }: DisplayContext
): string => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean")
    return t(
      value
        ? "contracts.updateTerms.change.yes"
        : "contracts.updateTerms.change.no"
    );
  if (DATE_FIELDS.has(field) && typeof value === "string")
    return formatDateShort(value);
  if (MONEY_FIELDS.has(field) && typeof value === "number")
    return formatCurrency(value, currency);
  // Mirrors the discount cell, which picks the unit the same way.
  if (field === "discountAmount" && typeof value === "number")
    return isDiscountPercentage ? `${value}%` : formatCurrency(value, currency);
  // The same helper the billing cell uses, so the tooltip agrees with the row.
  if (field === "customBillingFrequency" && typeof value === "number")
    return getBillingFrequencyLabel(value, t);
  if (
    field === "eqPaymentsBillingFrequency" &&
    typeof value === "number" &&
    isEqPeriod(value)
  )
    return t(EQ_PERIOD_LABEL_KEYS[value]);
  return String(value);
};
